import {Ref, onUnmounted, watch} from 'vue';
import {CoreGraphNodeId} from '../../../../../../@polygonjs/polygonjs/src/core/graph/CoreGraph';
import {StoreController} from '../../../../store/controllers/StoreController';
import {PolyDictionary} from '../../../../../../@polygonjs/polygonjs/src/types/GlobalTypes';
//
import {CodeData, CodeLanguage, CodeDisplayer} from '../helpers/updater/Common';
import {FragmentOnlyCodeDisplayer} from '../helpers/updater/FragmentOnly';
import {FunctionDataCodeDisplayer} from '../helpers/updater/FunctionData';
import {FunctionDataVelocityColliderCodeDisplayer} from '../helpers/updater/FunctionDataVelocityCollider';
import {VertexAndFragmentCodeDisplayer} from '../helpers/updater/VertexAndFragment';
import {MultiShaderCodeDisplayer} from '../helpers/updater/MultiShader';

// base
import type {BaseMatNodeType} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/mat/_Base';
// import {BaseGlShaderAssembler} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/gl/code/assemblers/_Base';
// types
// import type {BaseBuilderMatNodeType} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/mat/_BaseBuilder';
import type {BuilderCopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/cop/Builder';
import type {ParticlesSystemGpuSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/ParticlesSystemGpu';
// import type {ActorSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/Actor';
import type {ObjectBuilderSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/ObjectBuilder';
import type {TetSoftBodySolverSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/TetSoftBodySolver';
import {TypedActorSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/_BaseActor';
// import type {PointBuilderSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/PointBuilder';

export interface SetupUpdateCodeOptions {
	displayedNodeId: Ref<CoreGraphNodeId | null>;
	displayedShaderIndex: Ref<number>;
	shaderNames: Ref<string[]>;
	shadersByName: Ref<PolyDictionary<string>>;
	codeData: Ref<CodeData>;
}

export function SetupUpdateCode(options: SetupUpdateCodeOptions) {
	const {displayedNodeId, displayedShaderIndex, shaderNames, shadersByName, codeData} = options;

	onUnmounted(_unListenToChangesForCurrentNode);

	watch(displayedNodeId, (newId, oldId) => {
		if (oldId != null) {
			_unListenToChanges(oldId);
		}
		if (newId != null) {
			_listenToChanges(newId);
		}
		_updateCodePreview();
	});
	const DIRTY_CALLBACK_NAME = 'codePreviewPanel';
	function _listenToChanges(nodeId: CoreGraphNodeId) {
		const node = StoreController.engine.node(nodeId);
		if (!node) {
			console.warn('no node');
			return;
		}
		// listen to dirty
		node.dirtyController.addPostDirtyHook(DIRTY_CALLBACK_NAME, _updateCodePreview);

		// listen to compilation completed
		const compilationController = (node as TypedActorSopNode<any>).compilationController;
		if (compilationController) {
			compilationController.addOnCompilationCompleted(_updateCodePreview);
		}
	}
	function _unListenToChanges(nodeId: CoreGraphNodeId) {
		const node = StoreController.engine.node(nodeId);
		if (!node) {
			return;
		}

		// un-listen to dirty
		node.dirtyController.removePostDirtyHook(DIRTY_CALLBACK_NAME);

		// un-listen to compilation completed
		const compilationController = (node as TypedActorSopNode<any>).compilationController;
		if (compilationController) {
			compilationController.removeOnCompilationCompleted(_updateCodePreview);
		}
	}
	function _unListenToChangesForCurrentNode() {
		if (displayedNodeId.value == null) {
			return;
		}
		_unListenToChanges(displayedNodeId.value);
	}

	function _clearShaders() {
		shaderNames.value.splice(0, shaderNames.value.length);
		const namesInShaderByName = Object.keys(shadersByName.value);
		for (let shaderName of namesInShaderByName) {
			delete shadersByName.value[shaderName];
		}
	}

	async function _updateCodePreview() {
		const nodeId = displayedNodeId.value;
		if (!nodeId) {
			return;
		}
		const node = StoreController.engine.node(nodeId);
		if (!node) {
			return;
		}

		_clearShaders();

		switch (codeData.value.language) {
			case CodeLanguage.JS: {
				switch (codeData.value.displayer) {
					case CodeDisplayer.ACTOR: {
						await FunctionDataCodeDisplayer.update(
							node as ObjectBuilderSopNode,
							shadersByName,
							shaderNames
						);
						break;
					}
					case CodeDisplayer.ACTOR_VELOCITY_COLLIDER: {
						await FunctionDataVelocityColliderCodeDisplayer.update(
							node as TetSoftBodySolverSopNode,
							shadersByName,
							shaderNames
						);
						break;
					}
				}
			}
			case CodeLanguage.GL: {
				switch (codeData.value.displayer) {
					case CodeDisplayer.FRAGMENT_ONLY: {
						await FragmentOnlyCodeDisplayer.update(node as BuilderCopNode, shadersByName, shaderNames);
						break;
					}
					case CodeDisplayer.VERTEX_AND_FRAGMENT: {
						await VertexAndFragmentCodeDisplayer.update(
							node as BaseMatNodeType,
							shadersByName,
							shaderNames
						);
						break;
					}
					case CodeDisplayer.MULTI: {
						await MultiShaderCodeDisplayer.update(
							node as ParticlesSystemGpuSopNode,
							shadersByName,
							shaderNames
						);
						break;
					}
				}

				break;
			}
		}

		displayedShaderIndex.value = Math.min(displayedShaderIndex.value, shaderNames.value.length - 1);
	}

	return {};
}
